import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { bookingsAPI, venuesAPI } from '../services/api';
import { formatCurrency, formatDate } from '../utils/currency';
import { Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight, TrendingUp, Wallet, CreditCard, Building2, RefreshCw, X, CalendarDays } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import useFinancialVisibility from '../hooks/useFinancialVisibility';

const CalendarView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateBookings, setSelectedDateBookings] = useState([]);
  const [revenueDialogOpen, setRevenueDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const hideFinancials = useFinancialVisibility();
  
  // Ref for calendar
  const calendarRef = useRef(null);

  // Helper function to convert 24-hour time to 12-hour AM/PM format
  const formatTimeToAMPM = (time24) => {
    if (!time24 || time24 === '-') return '-';
    try {
      const [hours, minutes] = time24.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (e) {
      return time24;
    }
  };

  // Track if initial load is complete to prevent duplicate fetches
  const initialLoadDone = useRef(false);
  const lastPathname = useRef(location.pathname);
  const isMounted = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Initial load - only runs once
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      loadVenues();
      loadBookings();
    }
  }, []);

  // Reload bookings when month changes (not on venue change to avoid double fetch on mount)
  useEffect(() => {
    if (initialLoadDone.current) {
      loadBookings();
    }
  }, [currentDate]);

  // Reload bookings when page becomes visible or when navigating back to this page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadBookings();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Reload when navigating back to calendar page from another page
  useEffect(() => {
    if (lastPathname.current !== location.pathname && location.pathname === '/calendar') {
      loadBookings();
    }
    lastPathname.current = location.pathname;
  }, [location.pathname]);

  const loadBookings = async () => {
    if (!isMounted.current) return;
    setIsLoading(true);
    try {
      const data = await bookingsAPI.getAll();
      if (!isMounted.current) return; // Check again after async call
      
      // Map backend fields to frontend expected fields
      const mappedBookings = data.map(booking => ({
        id: booking.id,
        clientName: booking.customerName,
        clientEmail: booking.customerEmail,
        clientPhone: booking.customerPhone,
        venue: booking.venueName,
        venueName: booking.venueName,
        venueId: booking.venueId,
        bookingType: booking.status?.toLowerCase() || 'confirmed',
        status: booking.status?.toLowerCase() || 'confirmed',
        eventType: booking.eventType,
        eventStartDate: booking.eventDate,
        eventDate: booking.eventDate,
        eventEndDate: booking.eventEndDate || booking.eventDate, // Support multi-day bookings
        startTime: booking.startTime,
        endTime: booking.endTime,
        guestCount: booking.guests,
        requirements: booking.notes || '',
        totalAmount: booking.amount,
        amount: booking.amount,
        advanceAmount: booking.advancePaid || 0,
        advancePaid: booking.advancePaid || 0,
        payments: booking.payments || []
      }));
      setBookings(mappedBookings.filter(b => b.status !== 'cancelled'));
    } catch (error) {
      if (!isMounted.current) return; // Don't show error if unmounted
      console.error('Failed to load bookings from backend:', error);
      // Only show error toast if it's a real error, not a cancelled request
      if (error.name !== 'AbortError') {
        setBookings([]);
        toast({
          title: 'Error',
          description: 'Failed to load bookings. Please try refreshing.',
          variant: 'destructive'
        });
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  const loadVenues = async () => {
    if (!isMounted.current) return;
    try {
      const data = await venuesAPI.getAll();
      if (!isMounted.current) return; // Check again after async call
      
      setVenues(data);
      // Set first venue as default
      if (data.length > 0 && !selectedVenue) {
        setSelectedVenue(data[0].name);
      }
    } catch (error) {
      if (!isMounted.current) return; // Don't show error if unmounted
      console.error('Failed to load venues from backend:', error);
      // Only show error toast if it's a real error
      if (error.name !== 'AbortError') {
        setVenues([]);
        toast({
          title: 'Error',
          description: 'Failed to load venues. Please try refreshing.',
          variant: 'destructive'
        });
      }
    }
  };

  // Helper function to normalize date string to local date (handles timezone issues)
  // IMPORTANT: This must be defined BEFORE functions that use it
  const normalizeToLocalDate = (dateInput) => {
    if (!dateInput) return null;
    
    // If it's already a Date object, use it
    if (dateInput instanceof Date) {
      return new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate());
    }
    
    const dateStr = String(dateInput);
    
    // Handle ISO format with 'T' (e.g., "2026-03-25T00:00:00.000Z" or "2026-02-07T00:00:00")
    // Parse as local date to avoid timezone shift
    if (dateStr.includes('T')) {
      const datePart = dateStr.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    
    // Handle plain date format (e.g., "2026-04-15")
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    
    // Fallback: try standard Date parsing and normalize to local
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
    
    return null;
  };

  // Get bookings for current month with venue filter (single venue only)
  // IMPORTANT: Multi-day bookings are counted in the month when they START (first day)
  // Example: Feb 28 - Mar 1 booking is counted in February, not March
  const getBookingsForMonth = () => {
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    return bookings.filter(booking => {
      // Use START DATE only for month filtering - this is the commencement date
      const startDate = normalizeToLocalDate(booking.eventStartDate || booking.eventDate);
      if (!startDate) return false;
      const dateMatch = startDate.getMonth() === month && startDate.getFullYear() === year;
      const venueMatch = booking.venueName === selectedVenue;
      return dateMatch && venueMatch;
    });
  };

  // Calculate revenue breakdown
  const getMonthRevenueBreakdown = () => {
    const monthBookings = getBookingsForMonth();
    const totalRevenue = monthBookings.reduce((sum, b) => sum + (Number(b.totalAmount || b.amount) || 0), 0);
    
    const totalPaid = monthBookings.reduce((sum, b) => {
      const advance = Number(b.advanceAmount || b.advancePaid) || 0;
      const additionalPayments = (b.payments || []).reduce((pSum, p) => pSum + Number(p.amount), 0);
      return sum + advance + additionalPayments;
    }, 0);
    
    const balance = totalRevenue - totalPaid;
    
    // Event type breakdown
    const eventTypeBreakdown = monthBookings.reduce((acc, b) => {
      const eventType = b.eventType || 'Other';
      if (!acc[eventType]) {
        acc[eventType] = { count: 0, revenue: 0 };
      }
      acc[eventType].count += 1;
      acc[eventType].revenue += Number(b.totalAmount || b.amount) || 0;
      return acc;
    }, {});
    
    return { totalRevenue, totalPaid, balance, count: monthBookings.length, eventTypeBreakdown };
  };

  // Get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    return { daysInMonth, firstDayOfMonth };
  };

  // Get bookings for a specific date (includes multi-day bookings) - filtered by selected venue
  const getBookingsForDate = (day) => {
    const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const checkDateStr = checkDate.toDateString(); // Use string comparison for reliability
    
    return bookings.filter(booking => {
      // First check venue filter
      const venueMatch = booking.venueName === selectedVenue;
      if (!venueMatch) return false;
      
      // Normalize start date to local date
      const startDate = normalizeToLocalDate(booking.eventStartDate || booking.eventDate);
      if (!startDate) return false;
      
      // Normalize end date to local date (default to start date for single-day bookings)
      const endDate = normalizeToLocalDate(booking.eventEndDate) || startDate;
      
      // For single-day bookings, just compare date strings
      if (startDate.toDateString() === endDate.toDateString()) {
        return checkDateStr === startDate.toDateString();
      }
      
      // For multi-day bookings, check if checkDate falls within range (inclusive)
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  // Parse time string to hour decimal
  const parseTime = (timeStr) => {
    if (!timeStr) return 0;
    // Handle both 24-hour format (HH:MM) and 12-hour format (HH:MM AM/PM)
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      const [time, period] = timeStr.split(' ');
      let [hours, minutes] = time.split(':').map(Number);
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      return hours + minutes / 60;
    } else {
      // 24-hour format
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours + minutes / 60;
    }
  };

  // Calculate vertical position and height for time-based filling
  const calculateBookingPosition = (booking) => {
    if (!booking.startTime || !booking.endTime) {
      // Default to middle 50% of day if no time specified
      return { top: 25, height: 50 };
    }
    
    const startHour = parseTime(booking.startTime);
    const endHour = parseTime(booking.endTime);
    
    // Calculate top position as percentage from midnight (0:00)
    const topPosition = (startHour / 24) * 100;
    
    // Calculate height as percentage of the day
    const duration = endHour > startHour ? endHour - startHour : (24 - startHour) + endHour;
    const height = (duration / 24) * 100;
    
    return {
      top: Math.max(0, Math.min(topPosition, 100)),
      height: Math.max(2, Math.min(height, 100 - topPosition)) // Min 2% for visibility
    };
  };

  // Get color for booking type - matching mobile view colors
  const getBookingColor = (type) => {
    const normalizedType = (type || '').toLowerCase();
    if (normalizedType === 'blocked') return 'bg-yellow-400';
    if (normalizedType === 'confirmed') return 'bg-red-500 dark:bg-amber-600';
    if (normalizedType === 'available') return 'bg-green-400';
    return 'bg-red-500 dark:bg-amber-600'; // Default to confirmed color
  };

  // Calculate position and width for multi-day bookings
  const getBookingTimeSlot = (booking, currentDay) => {
    const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDay);
    
    // Use normalized dates for consistent comparison
    const startDate = normalizeToLocalDate(booking.eventStartDate || booking.eventDate);
    const endDate = normalizeToLocalDate(booking.eventEndDate) || startDate;
    
    const isFirstDay = checkDate.getTime() === startDate.getTime();
    const isLastDay = checkDate.getTime() === endDate.getTime();
    const isMiddleDay = !isFirstDay && !isLastDay;
    
    let startHour, endHour;
    
    if (isFirstDay && isLastDay) {
      // Single day booking - use actual times
      startHour = parseTime(booking.startTime);
      endHour = parseTime(booking.endTime);
    } else if (isFirstDay) {
      // First day of multi-day booking - start at actual time, end at midnight
      startHour = parseTime(booking.startTime);
      endHour = 24;
    } else if (isLastDay) {
      // Last day of multi-day booking - start at midnight, end at actual time
      startHour = 0;
      endHour = parseTime(booking.endTime);
    } else if (isMiddleDay) {
      // Middle day of multi-day booking - full day
      startHour = 0;
      endHour = 24;
    }
    
    let duration = endHour - startHour;
    
    // Handle edge case: if start and end time are the same (e.g., 15:00 - 15:00)
    // This can happen with multi-day bookings where the booking spans full days
    // Give it a minimum duration to be visible
    if (duration <= 0) {
      // If it's a multi-day booking showing on a single day, show full day coverage
      if (isFirstDay && !isLastDay) {
        // First day: from start time to end of day
        duration = 24 - startHour;
      } else if (isLastDay && !isFirstDay) {
        // Last day: from start of day to end time
        duration = endHour > 0 ? endHour : 24;
      } else {
        // Default: give minimum 1 hour visibility for same start/end time
        duration = Math.max(1, duration);
      }
    }
    
    const leftPosition = (startHour / 24) * 100;
    const widthPercent = Math.max((duration / 24) * 100, 4); // Minimum 4% width for visibility
    
    return { leftPosition, widthPercent, duration, isMultiDay: !isFirstDay || !isLastDay };
  };

  // Handle date click
  const handleDateClick = (day) => {
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(clickedDate);
    setSelectedDateBookings(getBookingsForDate(day));
  };

  // Navigate months
  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const { daysInMonth, firstDayOfMonth } = getDaysInMonth(currentDate);
  const monthBookings = getBookingsForMonth();
  const revenueBreakdown = getMonthRevenueBreakdown();

  // Create calendar grid
  const calendarDays = [];
  // Empty cells for days before month starts
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(<div key={`empty-${i}`} className="aspect-square"></div>);
  }
  // Actual days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 sm:gap-3 mb-1">
            <span className="text-lg sm:text-2xl font-bold text-teal-600 dark:text-primary flex items-center gap-1.5 border-b-2 border-teal-600 pb-1">
              <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
              CALENDAR
            </span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <a href="/year-view" className="text-lg sm:text-2xl font-bold text-gray-400 dark:text-muted-foreground flex items-center gap-1.5 hover:text-teal-600 dark:hover:text-primary transition-colors pb-1" data-testid="nav-year-view">
              <CalendarDays className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
              YEAR VIEW
            </a>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground mt-1">Visual timeline of your bookings and availability</p>
          <p className="text-[10px] sm:hidden text-gray-500 dark:text-muted-foreground mt-1">Tap dates for details</p>
        </div>
        <Button 
          onClick={() => {
            loadBookings();
            loadVenues();
          }}
          variant="outline"
          size="icon"
          className="h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0"
          disabled={isLoading}
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Month Stats - Side by side on mobile */}
      <div className="grid grid-cols-2 gap-2 sm:gap-6">
        <Card 
          className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
          data-testid="bookings-this-month-card"
          onClick={() => {
            // Navigate to monthly bookings page with this month and selected venue
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth() + 1;
            const venueParam = selectedVenue ? `&venue=${encodeURIComponent(selectedVenue)}` : '';
            navigate(`/monthly-bookings?month=${year}-${month.toString().padStart(2, '0')}${venueParam}`);
          }}
        >
          <CardContent className="p-2 sm:p-6 relative">
            <div className="absolute top-2 right-2 sm:hidden w-6 h-6 bg-teal-100 dark:bg-teal-900/30 rounded-md flex items-center justify-center">
              <CalendarIcon className="w-3 h-3 text-teal-600" />
            </div>
            <div className="hidden sm:block absolute top-4 right-4 w-12 h-12 bg-teal-100 dark:bg-teal-900/30 rounded-xl items-center justify-center sm:flex">
              <CalendarIcon className="w-6 h-6 text-teal-600" />
            </div>
            <p className="text-[10px] sm:text-sm font-medium text-gray-600 dark:text-muted-foreground pr-8 sm:pr-14">Bookings This Month</p>
            <p className="text-xl sm:text-3xl font-bold text-teal-600 mt-0.5 sm:mt-2">{monthBookings.length}</p>
            <p className="text-[9px] sm:text-xs text-gray-500 dark:text-muted-foreground mt-0.5 sm:hidden">Tap to view</p>
          </CardContent>
        </Card>
        
        {!hideFinancials ? (
          <Card 
            className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setRevenueDialogOpen(true)}
          >
            <CardContent className="p-2 sm:p-6 relative">
              <div className="absolute top-2 right-2 sm:hidden w-6 h-6 bg-orange-100 dark:bg-orange-900/30 rounded-md flex items-center justify-center">
                <TrendingUp className="w-3 h-3 text-orange-600" />
              </div>
              <div className="hidden sm:block absolute top-4 right-4 w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl items-center justify-center sm:flex">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <p className="text-[10px] sm:text-sm font-medium text-gray-600 dark:text-muted-foreground pr-8 sm:pr-14">Revenue This Month</p>
              <p className="text-base sm:text-3xl font-bold text-orange-600 mt-0.5 sm:mt-2 truncate">{formatCurrency(revenueBreakdown.totalRevenue)}</p>
              <p className="text-[9px] sm:text-xs text-gray-500 dark:text-muted-foreground mt-0.5">Tap for breakdown</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-md">
            <CardContent className="p-2 sm:p-6 relative">
              <div className="absolute top-2 right-2 sm:hidden w-6 h-6 bg-purple-100 dark:bg-purple-900/30 rounded-md flex items-center justify-center">
                <CalendarIcon className="w-3 h-3 text-purple-600" />
              </div>
              <div className="hidden sm:block absolute top-4 right-4 w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl items-center justify-center sm:flex">
                <CalendarIcon className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-[10px] sm:text-sm font-medium text-gray-600 dark:text-muted-foreground pr-8 sm:pr-14">Total Events</p>
              <p className="text-xl sm:text-3xl font-bold text-purple-600 mt-0.5 sm:mt-2">{monthBookings.length}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Shareable Calendar Section */}
      <div ref={calendarRef} className="space-y-4 bg-white dark:bg-card p-2 rounded-lg overflow-x-hidden">
        {/* Venue Name & Legend for Share */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <p className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-foreground whitespace-nowrap">Select Venue:</p>
                <Select value={selectedVenue} onValueChange={setSelectedVenue}>
                  <SelectTrigger className="w-full sm:w-[240px]">
                    <Building2 className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                    <SelectValue placeholder="Select a venue" />
                  </SelectTrigger>
                  <SelectContent>
                    {venues.map((venue) => (
                      <SelectItem key={venue.id} value={venue.name}>
                        {venue.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <p className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-foreground">Legend:</p>
                <div className="flex flex-wrap gap-3 sm:gap-4 lg:gap-6">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-5 h-2.5 sm:w-6 sm:h-3 bg-red-500 dark:bg-amber-600 rounded flex-shrink-0"></div>
                    <span className="text-[10px] sm:text-xs lg:text-sm text-gray-600 dark:text-muted-foreground whitespace-nowrap">Confirmed</span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-5 h-2.5 sm:w-6 sm:h-3 bg-yellow-400 rounded flex-shrink-0"></div>
                    <span className="text-[10px] sm:text-xs lg:text-sm text-gray-600 dark:text-muted-foreground whitespace-nowrap">Blocked</span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-5 h-2.5 sm:w-6 sm:h-3 bg-green-400 rounded flex-shrink-0"></div>
                    <span className="text-[10px] sm:text-xs lg:text-sm text-gray-600 dark:text-muted-foreground whitespace-nowrap">Available</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Custom Calendar */}
        <Card className="border-0 shadow-md">
          <CardHeader className="p-4 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-foreground truncate">
                {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </CardTitle>
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                {/* Month Navigation */}
                <div className="flex gap-1 sm:gap-2">
                  <Button variant="outline" size="icon" onClick={previousMonth} className="h-8 w-8 sm:h-10 sm:w-10">
                    <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={nextMonth} className="h-8 w-8 sm:h-10 sm:w-10">
                    <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
        <CardContent className="p-2 sm:p-4 lg:p-6 overflow-x-auto">
          {/* Day labels - Desktop */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-7 gap-3 mb-2">
              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
                <div key={day} className="text-center font-semibold text-gray-600 dark:text-muted-foreground text-sm py-2">
                  {day}
                </div>
              ))}
            </div>
          </div>
          
          {/* Day labels - Mobile/Tablet */}
          <div className="grid grid-cols-7 gap-0.5 sm:gap-2 md:gap-3 mb-2 min-w-0 lg:hidden">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="text-center font-semibold text-gray-600 dark:text-muted-foreground text-[10px] sm:text-xs md:text-sm py-1 sm:py-2">
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{day.charAt(0)}</span>
              </div>
            ))}
          </div>

          {/* Calendar grid - Desktop */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-7 gap-3">
              {calendarDays.map((day, index) => {
                if (typeof day !== 'number') return <div key={`empty-desktop-${index}`} className="h-24"></div>;
              
              const dayBookings = getBookingsForDate(day);
              const isToday = new Date().getDate() === day && 
                             new Date().getMonth() === currentDate.getMonth() && 
                             new Date().getFullYear() === currentDate.getFullYear();
              const hasBookings = dayBookings.length > 0;
              
              return (
                <div
                  key={`desktop-${day}`}
                  onClick={() => handleDateClick(day)}
                  className={`
                    h-24 border rounded-xl cursor-pointer transition-all hover:shadow-lg relative overflow-hidden
                    ${isToday ? 'border-teal-500 border-2 ring-2 ring-teal-200' : 'border-gray-200 dark:border-border hover:border-gray-300 dark:border-border'}
                    ${selectedDate?.getDate() === day && selectedDate?.getMonth() === currentDate.getMonth() ? 'ring-2 ring-blue-400' : ''}
                  `}
                >
                  {/* Date number */}
                  <div className={`absolute top-1 left-2 text-sm font-bold z-10 ${isToday ? 'text-teal-600' : 'text-gray-700 dark:text-foreground'}`}>
                    {day}
                  </div>
                  
                  {/* Time-based availability visualization - matching mobile view exactly */}
                  <div className="absolute inset-0 top-6 bg-green-100 dark:bg-green-900/30 overflow-hidden">
                    {/* Default available background (full day) - same as mobile */}
                    <div className="absolute inset-0 bg-green-400"></div>
                    
                    {/* Bookings positioned at their actual time slots - same logic as mobile */}
                    {dayBookings
                      .map((booking, idx) => ({
                        ...booking,
                        originalIndex: idx,
                        ...getBookingTimeSlot(booking, day)
                      }))
                      .sort((a, b) => a.leftPosition - b.leftPosition)
                      .map((booking, idx, sortedBookings) => {
                        const { leftPosition, widthPercent, duration, isMultiDay, originalIndex } = booking;
                        const bookingType = booking.bookingType || booking.status || 'confirmed';
                        const isLastBooking = idx === sortedBookings.length - 1;
                        const nextBooking = !isLastBooking ? sortedBookings[idx + 1] : null;
                        
                        // Check if next booking is immediately adjacent (continuous)
                        const hasPartition = nextBooking && Math.abs((leftPosition + widthPercent) - nextBooking.leftPosition) < 0.5;
                        
                        return (
                          <div
                            key={originalIndex}
                            className={`absolute ${getBookingColor(bookingType)} flex items-center justify-center`}
                            style={{
                              left: `${leftPosition}%`,
                              width: `${widthPercent}%`,
                              top: 0,
                              bottom: 0,
                              minWidth: widthPercent > 4 ? '8px' : '4px',
                              borderRight: hasPartition ? '2px solid black' : 'none'
                            }}
                            title={`${booking.eventType || 'Booking'}\n${formatTimeToAMPM(booking.startTime)} - ${formatTimeToAMPM(booking.endTime)}\n${booking.customerName || booking.clientName || ''}\n${Math.round(widthPercent)}% (${duration.toFixed(1)}h)${isMultiDay ? ' (Multi-day)' : ''}`}
                          >
                            {/* Show percentage inside bar for desktop */}
                            {widthPercent >= 15 && (
                              <div className="text-[10px] font-bold text-white drop-shadow">
                                {Math.round(widthPercent)}%
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                  
                  {/* Booking count badge */}
                  {hasBookings && (
                    <div className="absolute bottom-1 right-1 bg-red-50 dark:bg-red-900/200 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center z-10">
                      {dayBookings.length}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          </div>
          
          {/* Calendar grid - Mobile/Tablet (no zoom) */}
          <div className="grid grid-cols-7 gap-0.5 sm:gap-1 md:gap-2 lg:hidden">
            {calendarDays.map((day, index) => {
              if (typeof day !== 'number') return day;
              
              const dayBookings = getBookingsForDate(day);
              const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
              const isSelected = selectedDate && selectedDate.getDate() === day && selectedDate.getMonth() === currentDate.getMonth();

              return (
                <div
                  key={day}
                  onClick={() => handleDateClick(day)}
                  className={`aspect-square border sm:border-2 rounded-sm sm:rounded-lg p-0.5 sm:p-1 md:p-2 cursor-pointer transition-all hover:shadow-md overflow-hidden ${
                    isToday ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 
                    isSelected ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 
                    'border-gray-200 dark:border-border hover:border-gray-300 dark:border-border bg-white dark:bg-card'
                  }`}
                >
                  <div className="h-full flex flex-col relative">
                    {/* Date number with booking count */}
                    <div className="flex items-center justify-between mb-0.5 sm:mb-1 gap-1">
                      <div className="text-[10px] sm:text-xs md:text-sm font-bold text-gray-700 dark:text-foreground leading-none">
                        {day}
                      </div>
                      {/* Show booking count next to date if multiple */}
                      {dayBookings.length > 1 && (
                        <div className="text-[7px] sm:text-[8px] text-white bg-gray-700 px-1 rounded leading-tight font-medium">
                          {dayBookings.length}
                        </div>
                      )}
                    </div>
                    
                    {/* Horizontal color bars positioned by actual time */}
                    <div className="flex-1 relative bg-green-100 dark:bg-green-900/30 overflow-hidden">
                      {/* Default available background (full day) */}
                      <div className="absolute inset-0 bg-green-400"></div>
                      
                      {/* Bookings positioned at their actual time slots */}
                      {dayBookings
                        .map((booking, idx) => ({
                          ...booking,
                          originalIndex: idx,
                          ...getBookingTimeSlot(booking, day)
                        }))
                        .sort((a, b) => a.leftPosition - b.leftPosition) // Sort by start position
                        .map((booking, idx, sortedBookings) => {
                          const { leftPosition, widthPercent, duration, isMultiDay, originalIndex } = booking;
                          const bookingType = booking.bookingType || booking.status || 'confirmed';
                          const isLastBooking = idx === sortedBookings.length - 1;
                          const nextBooking = !isLastBooking ? sortedBookings[idx + 1] : null;
                          
                          // Check if next booking is immediately adjacent (continuous)
                          const hasPartition = nextBooking && Math.abs((leftPosition + widthPercent) - nextBooking.leftPosition) < 0.5;
                          
                          return (
                            <div
                              key={originalIndex}
                              className={`absolute ${getBookingColor(bookingType)} flex items-center justify-center`}
                              style={{
                                left: `${leftPosition}%`,
                                width: `${widthPercent}%`,
                                height: '100%',
                                minWidth: widthPercent > 4 ? '8px' : '4px',
                                borderRight: hasPartition ? '2px solid black' : 'none'
                              }}
                              title={`${booking.eventType || 'Booking'}\n${formatTimeToAMPM(booking.startTime)} - ${formatTimeToAMPM(booking.endTime)}\n${booking.customerName || booking.clientName || ''}\n${Math.round(widthPercent)}% (${duration.toFixed(1)}h)${isMultiDay ? ' (Multi-day)' : ''}`}
                            >
                              {/* Show percentage rotated vertically inside bar */}
                              {widthPercent >= 10 && (
                                <div 
                                  className="hidden sm:flex items-center justify-center h-full"
                                >
                                  <div 
                                    className="text-[7px] sm:text-[8px] md:text-[10px] font-bold text-white drop-shadow"
                                    style={{
                                      transform: 'rotate(-90deg)',
                                      whiteSpace: 'nowrap',
                                      transformOrigin: 'center center'
                                    }}
                                  >
                                    {Math.round(widthPercent)}%
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      </div> {/* End of shareable calendar section */}

      {/* Selected Date Bookings - Only shows when clicking a specific date */}
      {selectedDate && selectedDateBookings.length > 0 && (
        <Card className="border-0 shadow-md">
          <CardHeader className="p-4 sm:p-6 flex flex-row items-center justify-between">
            <CardTitle className="text-base sm:text-lg dark:text-foreground">
              Bookings for {selectedDate.toLocaleDateString('default', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setSelectedDate(null);
                setSelectedDateBookings([]);
              }}
              className="h-7 w-7 rounded-full hover:bg-gray-100 dark:bg-secondary"
            >
              <X className="h-4 w-4 text-gray-500 dark:text-muted-foreground" />
            </Button>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            {selectedDateBookings.length > 0 ? (
              <div className="space-y-3 sm:space-y-4 max-h-[60vh] overflow-y-auto">
                {selectedDateBookings
                  .sort((a, b) => new Date(a.eventStartDate || a.eventDate) - new Date(b.eventStartDate || b.eventDate))
                  .map((booking, idx) => {
                  // Check if booking is completed
                  const isCompleted = (() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const eventDate = new Date(booking.eventEndDate || booking.eventStartDate || booking.eventDate);
                    eventDate.setHours(23, 59, 59, 999);
                    const status = (booking.bookingType || 'confirmed').toLowerCase();
                    return status === 'confirmed' && eventDate < today;
                  })();
                  
                  return (
                  <div key={idx} className="p-4 sm:p-6 bg-gradient-to-br from-white to-gray-50 dark:from-card dark:to-secondary rounded-lg border-2 border-gray-200 dark:border-border shadow-sm hover:shadow-md transition-shadow relative">
                    {/* Completed Stamp Watermark */}
                    {isCompleted && (
                      <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 pointer-events-none">
                        <div className="px-2 py-1 sm:px-3 sm:py-1.5 border-2 border-orange-500 rounded transform rotate-[-15deg] opacity-70">
                          <span className="text-orange-500 font-bold text-[10px] sm:text-xs uppercase tracking-wider">Completed</span>
                        </div>
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col xs:flex-row xs:items-center gap-2">
                          <h4 className="font-bold text-base sm:text-lg text-gray-900 dark:text-foreground truncate">{booking.customerName || booking.clientName}</h4>
                          <Badge className={`${booking.bookingType === 'blocked' ? 'bg-yellow-400 text-yellow-900' : 'bg-red-500 text-white'} text-xs font-semibold w-fit`}>
                            {booking.bookingType === 'blocked' ? '🚫 Blocked' : '✓ Confirmed'}
                          </Badge>
                        </div>
                        <p className="text-sm sm:text-base text-gray-700 dark:text-foreground font-medium mt-1">{booking.eventType}</p>
                      </div>
                    </div>
                    
                    {/* Date and Time Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-muted-foreground font-medium">From Date</p>
                            <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-foreground">
                              {formatDate(booking.eventStartDate || booking.eventDate)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-muted-foreground font-medium">From Time</p>
                            <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-foreground">{formatTimeToAMPM(booking.startTime)}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-muted-foreground font-medium">To Date</p>
                            <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-foreground">
                              {formatDate(booking.eventEndDate || booking.eventDate)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-muted-foreground font-medium">To Time</p>
                            <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-foreground">{formatTimeToAMPM(booking.endTime)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Venue and Guest Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 pt-3 border-t border-gray-200 dark:border-border">
                      <div className="flex items-start gap-2">
                        <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-500 dark:text-muted-foreground font-medium">Venue</p>
                          <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-foreground">{booking.venueName || booking.venue}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-muted-foreground font-medium">Guests</p>
                          <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-foreground">{booking.guestCount || booking.guests || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Contact and Payment Details */}
                    <div className={`grid grid-cols-1 ${!hideFinancials ? 'md:grid-cols-2' : ''} gap-3 sm:gap-4 pt-3 border-t border-gray-200 dark:border-border`}>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-muted-foreground font-medium">Contact</p>
                          <p className="text-sm sm:text-base text-gray-900 dark:text-foreground">{booking.clientPhone || 'N/A'}</p>
                          {booking.clientEmail && (
                            <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground truncate">{booking.clientEmail}</p>
                          )}
                        </div>
                      </div>
                      {!hideFinancials && (() => {
                        const totalAmount = Number(booking.totalAmount || booking.amount) || 0;
                        const advanceAmount = Number(booking.advanceAmount || booking.advancePaid) || 0;
                        const additionalPayments = (booking.payments || []).reduce((sum, p) => sum + Number(p.amount), 0);
                        const totalPaid = advanceAmount + additionalPayments;
                        const balanceDue = totalAmount - totalPaid;
                        
                        return (
                          <div className="space-y-1">
                            <div className="flex justify-between items-center">
                              <p className="text-xs text-gray-500 dark:text-muted-foreground font-medium">Total Amount</p>
                              <p className="text-sm sm:text-base font-bold text-gray-900 dark:text-foreground">{formatCurrency(totalAmount)}</p>
                            </div>
                            <div className="flex justify-between items-center">
                              <p className="text-xs text-gray-500 dark:text-muted-foreground font-medium">Paid</p>
                              <p className="text-sm sm:text-base font-semibold text-green-600">{formatCurrency(totalPaid)}</p>
                            </div>
                            <div className="flex justify-between items-center pt-1 border-t border-gray-200 dark:border-border">
                              <p className="text-xs text-gray-500 dark:text-muted-foreground font-medium">Balance Due</p>
                              <p className="text-sm sm:text-base font-bold text-orange-600">
                                {formatCurrency(balanceDue)}
                              </p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* Notes/Requirements */}
                    {booking.requirements && (
                      <div className="mt-4 pt-3 border-t border-gray-200 dark:border-border">
                        <p className="text-xs text-gray-500 dark:text-muted-foreground font-medium mb-1">Notes/Requirements</p>
                        <p className="text-sm text-gray-700 dark:text-foreground bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded border border-yellow-200 dark:border-yellow-800">{booking.requirements}</p>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <CalendarIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 dark:text-muted-foreground mx-auto mb-3 sm:mb-4" />
                <p className="text-sm sm:text-base text-gray-600 dark:text-muted-foreground">No bookings on this date</p>
                <p className="text-sm sm:text-base text-green-600 font-medium mt-2">Date is available for booking</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Revenue Breakdown Dialog */}
      <Dialog open={revenueDialogOpen} onOpenChange={setRevenueDialogOpen}>
        <DialogContent className="w-full sm:max-w-md overflow-y-auto p-3 sm:p-6 [&>button]:hidden">
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setRevenueDialogOpen(false)}
              className="absolute -top-1 right-0 h-7 w-7 rounded-full hover:bg-gray-100 dark:bg-secondary z-10"
            >
              <X className="h-4 w-4 text-gray-500 dark:text-muted-foreground" />
            </Button>
            <div className="flex items-center gap-2 text-sm sm:text-lg font-semibold mb-3 pr-8">
              <TrendingUp className="w-4 h-4 text-orange-600 flex-shrink-0" />
              <span className="truncate">Revenue - {currentDate.toLocaleString('default', { month: 'short', year: 'numeric' })}</span>
            </div>

            <div className="space-y-3">
              {/* Top 3 Cards - Compact Grid */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg border border-blue-200 text-center">
                  <TrendingUp className="w-4 h-4 text-blue-600 mx-auto mb-1" />
                  <p className="text-[9px] sm:text-xs text-gray-600 dark:text-muted-foreground">Total Revenue</p>
                  <p className="text-sm sm:text-base font-bold text-blue-700 truncate">
                    {formatCurrency(revenueBreakdown.totalRevenue)}
                  </p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded-lg border border-green-200 text-center">
                  <Wallet className="w-4 h-4 text-green-600 mx-auto mb-1" />
                  <p className="text-[9px] sm:text-xs text-gray-600 dark:text-muted-foreground">Received</p>
                  <p className="text-sm sm:text-base font-bold text-green-700 truncate">
                    {formatCurrency(revenueBreakdown.totalPaid)}
                  </p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 p-2 rounded-lg border border-orange-200 text-center">
                  <CreditCard className="w-4 h-4 text-orange-600 mx-auto mb-1" />
                  <p className="text-[9px] sm:text-xs text-gray-600 dark:text-muted-foreground">Balance</p>
                  <p className="text-sm sm:text-base font-bold text-orange-700 truncate">
                    {formatCurrency(revenueBreakdown.balance)}
                  </p>
                </div>
              </div>

              {/* Payment Progress */}
              <div className="bg-gray-50 dark:bg-secondary p-2 rounded-lg">
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-gray-600 dark:text-muted-foreground">Payment Progress</span>
                  <span className="font-semibold text-gray-800 dark:text-foreground">
                    {revenueBreakdown.totalRevenue > 0 
                      ? Math.round((revenueBreakdown.totalPaid / revenueBreakdown.totalRevenue) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full transition-all"
                    style={{ 
                      width: `${revenueBreakdown.totalRevenue > 0 
                        ? Math.min((revenueBreakdown.totalPaid / revenueBreakdown.totalRevenue) * 100, 100)
                        : 0}%` 
                    }}
                  ></div>
                </div>
              </div>

              {/* Event Types Breakdown */}
              <div className="border-t pt-2">
                <p className="text-xs font-semibold text-gray-700 dark:text-foreground mb-2">Events by Type</p>
                <div className="space-y-1.5">
                  {Object.entries(revenueBreakdown.eventTypeBreakdown || {})
                    .sort((a, b) => b[1].revenue - a[1].revenue)
                    .map(([eventType, data]) => (
                      <div key={eventType} className="flex items-center justify-between bg-white dark:bg-card p-2 rounded border text-xs">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="w-2 h-2 rounded-full bg-teal-50 dark:bg-teal-900/200 flex-shrink-0"></span>
                          <span className="truncate text-gray-700 dark:text-foreground">{eventType}</span>
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                            {data.count}
                          </Badge>
                        </div>
                        <span className="font-semibold text-gray-900 dark:text-foreground ml-2 flex-shrink-0">
                          {formatCurrency(data.revenue)}
                        </span>
                      </div>
                    ))}
                  {Object.keys(revenueBreakdown.eventTypeBreakdown || {}).length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-muted-foreground text-center py-2">No events this month</p>
                  )}
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div className="bg-gray-50 dark:bg-secondary p-2 rounded text-center">
                  <p className="text-[9px] sm:text-xs text-gray-600 dark:text-muted-foreground">Total Bookings</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-foreground">{revenueBreakdown.count}</p>
                </div>
                <div className="bg-gray-50 dark:bg-secondary p-2 rounded text-center">
                  <p className="text-[9px] sm:text-xs text-gray-600 dark:text-muted-foreground">Avg. Value</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-foreground truncate">
                    {formatCurrency(revenueBreakdown.count > 0 
                      ? revenueBreakdown.totalRevenue / revenueBreakdown.count 
                      : 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarView;
